import React from "react";
import {
  GridAggregationModel,
  GridColumnMenu,
  isAutogeneratedRow,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Box, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import { AdditionalDocumentDTO } from "../../types/AdditionalDocumentDTO";
import { createAdditionalDocumentsColumns } from "./additionalDocumentsColumns";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import StripedDataGrid from "../common/StripedDataGrid";
import AdditionalDocumentsToolbar from "./AdditionalDocumentsToolbar";
import { usePersistedGridState } from "../../hooks/usePersistedGridState";
import { applySavedColumnOrder } from "../../utils/gridStatePersistence";

type Props = {
  gridStorageKey: string;
  documents: AdditionalDocumentDTO[];
  onSelect: (doc: AdditionalDocumentDTO) => void;
  onDownload: (doc: AdditionalDocumentDTO) => void;
  onDelete: (doc: AdditionalDocumentDTO) => void;
  onAdd: () => void;
};

function getAggregationModelFromInitialState(
  initialState: any
): GridAggregationModel {
  const model = initialState?.aggregation?.model;

  if (!model || typeof model !== "object") {
    return {};
  }

  return model;
}

type ClearAggregationMenuItemProps = {
  colDef?: {
    field?: string;
  };
  hideMenu?: (event: React.MouseEvent) => void;
  aggregationModel?: GridAggregationModel;
  onAggregationModelChange?: (model: GridAggregationModel) => void;
};

function ClearAggregationMenuItem(props: ClearAggregationMenuItemProps) {
  const {
    colDef,
    hideMenu,
    aggregationModel = {},
    onAggregationModelChange,
  } = props;

  const field = colDef?.field;

  if (!field || !aggregationModel[field]) {
    return null;
  }

  return (
    <MenuItem
      onClick={(event) => {
        event.stopPropagation();

        const nextModel = { ...aggregationModel };
        delete nextModel[field];

        onAggregationModelChange?.(nextModel);
        hideMenu?.(event);
      }}
    >
      <ListItemIcon>
        <ClearIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Usuń agregację</ListItemText>
    </MenuItem>
  );
}

function AdditionalDocumentsColumnMenu(props: any) {
  const {
    aggregationModel,
    onAggregationModelChange,
    ...columnMenuProps
  } = props;

  return (
    <GridColumnMenu
      {...columnMenuProps}
      slots={{
        columnMenuClearAggregationItem: ClearAggregationMenuItem as any,
      }}
      slotProps={{
        columnMenuClearAggregationItem: {
          displayOrder: 70,
          aggregationModel,
          onAggregationModelChange,
        } as any,
      }}
    />
  );
}

export default function AdditionalDocumentsGrid({
  gridStorageKey,
  documents,
  onSelect,
  onDownload,
  onDelete,
  onAdd,
}: Props) {
  const { t } = useAppTranslation("common");
  const apiRef = useGridApiRef();

  const { initialState, scheduleSave } = usePersistedGridState(
    apiRef,
    gridStorageKey
  );

  const [aggregationModel, setAggregationModel] =
    React.useState<GridAggregationModel>(() =>
      getAggregationModelFromInitialState(initialState)
    );

  const aggregationSaveReadyRef = React.useRef(false);

  React.useEffect(() => {
    aggregationSaveReadyRef.current = false;
    setAggregationModel(getAggregationModelFromInitialState(initialState));
  }, [gridStorageKey, initialState]);

  React.useEffect(() => {
    if (!aggregationSaveReadyRef.current) {
      aggregationSaveReadyRef.current = true;
      return;
    }

    scheduleSave();
  }, [aggregationModel, scheduleSave]);

  const handleAggregationModelChange = React.useCallback(
    (model: GridAggregationModel) => {
      setAggregationModel(model);
    },
    []
  );

  const baseColumns = React.useMemo(
    () =>
      createAdditionalDocumentsColumns(t, {
        onDownload,
        onDelete,
      }),
    [t, onDownload, onDelete]
  );

  const columns = React.useMemo(
    () => applySavedColumnOrder(baseColumns, initialState),
    [baseColumns, initialState]
  );

  const handleScheduleSave = React.useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  const isRealAdditionalDocumentRow = React.useCallback((params: any) => {
    if (!params?.row) return false;
    if (isAutogeneratedRow(params.row)) return false;
    if (!params.row.SystemId) return false;

    return true;
  }, []);

  const handleRowClick = React.useCallback(
    (params: any) => {
      if (!isRealAdditionalDocumentRow(params)) {
        return;
      }

      onSelect(params.row);
    },
    [isRealAdditionalDocumentRow, onSelect]
  );

  const mergedInitialState = React.useMemo(
    () => ({
      ...initialState,
      pagination: {
        paginationModel: {
          pageSize: 25,
          page: 0,
        },
        ...(initialState as any)?.pagination,
      },
      sorting: {
        sortModel: [
          {
            field: "systemCreatedAt",
            sort: "desc",
          },
        ],
        ...(initialState as any)?.sorting,
      },
    }),
    [initialState]
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <StripedDataGrid
        key={gridStorageKey}
        apiRef={apiRef}
        initialState={mergedInitialState}
        showToolbar
        rows={documents}
        columns={columns}
        getRowId={(row) => row.SystemId}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick
        pagination
        paginationMode="client"
        pageSizeOptions={[10, 25, 50, 100]}
        aggregationModel={aggregationModel}
        onAggregationModelChange={handleAggregationModelChange}
        aggregationRowsScope="filtered"
        disableColumnPinning
        disablePivoting
        onPaginationModelChange={handleScheduleSave}
        onSortModelChange={handleScheduleSave}
        onFilterModelChange={handleScheduleSave}
        onColumnOrderChange={handleScheduleSave}
        onColumnWidthChange={handleScheduleSave}
        onColumnVisibilityModelChange={handleScheduleSave}
        onDensityChange={handleScheduleSave}
        onPreferencePanelClose={handleScheduleSave}
        slots={{
          toolbar: AdditionalDocumentsToolbar as any,
          columnMenu: AdditionalDocumentsColumnMenu as any,
        }}
        slotProps={{
          columnMenu: {
            aggregationModel,
            onAggregationModelChange: handleAggregationModelChange,
          } as any,
          toolbar: {
            onAdd,
          } as any,
        }}
        sx={{
          flex: 1,
          "& .actions-cell": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      />
    </Box>
  );
}